import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { ContactField } from '@heybesafe/shared';
import { ensureUrlScheme } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import LoadingPane from '../../components/LoadingPane';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { ContactAvatar } from '../../components/ContactAvatar';
import { HbsButton } from '../../components/hbs/Button';
import { Icon } from '../../components/hbs/Icon';

/**
 * Read-only detail for a family-shared contact, reached by clicking a contact
 * tile in the family Binder (a category page or the Family-contacts catch-all).
 * Mirrors the personal `ContactDetailPage` view.
 */
function FieldRows({
  title,
  fields,
  href,
}: {
  title: string;
  fields?: ContactField[];
  href?: (value: string) => string;
}): JSX.Element | null {
  const rows = (fields ?? []).filter((f) => f.value.trim());
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
        {title}
      </div>
      <ul className="flex flex-col gap-1">
        {rows.map((f, i) => (
          <li key={`${f.label}-${i}`} className="flex items-baseline gap-2">
            {f.label && <span className="text-[12px] text-hbs-text-mute w-16 flex-shrink-0">{f.label}</span>}
            {href ? (
              <a href={href(f.value)} className="text-[14px] text-hbs-primary hover:underline break-all">
                {f.value}
              </a>
            ) : (
              <span className="text-[14px] text-hbs-text break-all">{f.value}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function FamilyContactDetailPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId?: string; itemId: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, orgs = [], deleteItem, sync } = useSafe();
  const categoriesByOrg = useFamilyCategories();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const item = items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'contact');
  const org = orgs.find((o) => o.id === orgId);
  // Deleting a family-shared item is admin-only server-side (a member's
  // tombstone of an ORG# item is rejected), so only surface Delete to admins.
  const isAdmin = org?.role === 'admin';
  const category = categoryId
    ? (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === categoryId)
    : undefined;
  const backTo = categoryId ? `/orgs/${orgId}/binder/${categoryId}` : `/orgs/${orgId}/binder/contacts`;
  const backLabel = category?.name ?? (categoryId ? 'category' : 'Family contacts');

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading contact…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Contact not found.</p>
        <HbsButton variant="plain" id="family-contact-detail-not-found-back-button" onClick={() => navigate(backTo)}>Back</HbsButton>
      </div>
    );
  }

  const subtitle = [item.role, item.company].filter(Boolean).join(' · ');
  const websiteHref = item.website ? ensureUrlScheme(item.website) : '';
  const hasDetails = !!(item.phones?.length || item.emails?.length || item.addresses?.length || item.website || item.notes);

  async function handleDelete(): Promise<void> {
    if (!item) return;
    setDeleting(true);
    try {
      await deleteItem(item.id);
      await sync();
      navigate(backTo);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      <div>
        <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
          {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
        </p>
        <div className="flex items-start gap-4 mt-2">
          <ContactAvatar name={item.name} size={64} />
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text truncate">
              {item.name}
            </h1>
            <p className="text-[13px] text-hbs-text-mute mt-1">{subtitle || 'Contact'}</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <Link
              to={categoryId ? `/orgs/${orgId}/binder/${categoryId}/contacts/${itemId}/edit` : `/orgs/${orgId}/binder/contacts/${itemId}/edit`}
              className="text-[13px] font-semibold text-hbs-primary hover:underline"
            >
              Edit
            </Link>
            {isAdmin && !confirming && (
              <button
                type="button"
              id="family-contact-delete-button"
              onClick={() => setConfirming(true)}
                className="text-[13px] font-semibold text-hbs-warning hover:underline"
              >
                Delete
              </button>
            )}
            {isAdmin && confirming && (
              <span className="inline-flex items-center gap-2 text-[13px]">
                <span className="text-hbs-text-mute">Delete this contact?</span>
                <button
                  type="button"
                id="family-contact-delete-cancel-button"
                onClick={() => setConfirming(false)}
                  className="font-semibold text-hbs-text hover:underline"
                >
                  Cancel
                </button>
                <button
                  type="button"
                id="family-contact-delete-confirm-button"
                onClick={handleDelete}
                  disabled={deleting}
                  className="font-semibold text-hbs-warning hover:underline disabled:opacity-60"
                >
                  {deleting ? 'Deleting…' : 'Confirm delete'}
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex flex-col gap-6">
        {hasDetails ? (
          <>
            <FieldRows title="Phone" fields={item.phones} href={(v) => `tel:${v.replace(/\s+/g, '')}`} />
            <FieldRows title="Email" fields={item.emails} href={(v) => `mailto:${v}`} />
            <FieldRows title="Address" fields={item.addresses} />
            {item.website && (
              <div className="flex flex-col gap-2">
                <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">Website</div>
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline break-all"
                >
                  <Icon name="launch" size={14} />
                  {item.website}
                </a>
              </div>
            )}
            {item.notes && (
              <div className="flex flex-col gap-2">
                <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">Notes</div>
                <p className="text-[14px] text-hbs-text whitespace-pre-wrap">{item.notes}</p>
              </div>
            )}
          </>
        ) : (
          <p className="text-[13px] text-hbs-text-mute">No contact details.</p>
        )}
      </section>

      <div>
        <HbsButton variant="plain" id="family-contact-back-button" onClick={() => navigate(backTo)}>Back to {backLabel}</HbsButton>
      </div>
    </div>
  );
}
